import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  ArrowLeft, TrendingUp, Users, CreditCard, Activity, 
  BarChart3, Clock, Globe, Zap, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

/**
 * InvestorDashboard - Admin-only business metrics view
 * Shows key metrics for investors and stakeholders
 */
const InvestorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  // Admin check
  const isAdmin = user?.email === 'admin@lifeflo.ai' || user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) {
      navigate('/home');
      return;
    }
    fetchMetrics();
  }, [timeRange, isAdmin, navigate]);

  const fetchMetrics = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/metrics?time_range=${timeRange}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      } else {
        // Show empty/zero data structure if API fails
        setMetrics(getEmptyMetrics());
      }
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      setMetrics(getEmptyMetrics());
    } finally {
      setLoading(false);
    }
  };

  // Empty metrics structure - shows real zeros instead of fake data
  const getEmptyMetrics = () => ({
    overview: {
      total_users: 0,
      active_users: 0,
      premium_users: 0,
      trial_users: 0,
      conversion_rate: 0
    },
    revenue: {
      mrr: 0,
      arr: 0,
      monthly_subscribers: 0,
      annual_subscribers: 0,
      growth_rate: 0,
      churn_rate: 0
    },
    retention: {
      day_1: 0,
      day_7: 0,
      day_30: 0
    },
    engagement: {
      dau: 0,
      mau: 0,
      avg_session: 0,
      retention_7d: 0,
      retention_30d: 0
    },
    features: {
      allora_chats: 0,
      water_logs: 0,
      tasks_completed: 0,
      health_entries: 0
    },
    growth: []
  });

  if (!isAdmin) {
    return null;
  }

  const MetricCard = ({ title, value, change, icon: Icon, format = 'number' }) => {
    const isPositive = change >= 0;
    const safeValue = value || 0;
    const formattedValue = format === 'currency' 
      ? `$${safeValue.toLocaleString()}` 
      : format === 'percent' 
        ? `${safeValue}%`
        : safeValue.toLocaleString();
        
    return (
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
        <div className="flex items-start justify-between mb-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${theme?.colors?.primary}15` }}>
            <Icon className="w-5 h-5" style={{ color: theme?.colors?.primary }} />
          </div>
          {change !== undefined && (
            <div className={`flex items-center gap-0.5 text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        <p className="text-2xl font-bold text-gray-900">{formattedValue}</p>
        <p className="text-sm text-gray-500 mt-1">{title}</p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
        style={{ backgroundColor: theme?.colors?.background || '#f8f5ff' }}>
        <div className="animate-spin w-8 h-8 border-4 border-gray-200 rounded-full"
          style={{ borderTopColor: theme?.colors?.primary }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme?.colors?.background || '#f8f5ff' }}>
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-100 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Investor Dashboard</h1>
              <p className="text-xs text-gray-500">Business metrics & analytics</p>
            </div>
          </div>
          
          {/* Time Range Selector */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
            {['7d', '30d', '90d'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  timeRange === range 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Key Metrics */}
        <div>
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard 
              title="Total Users" 
              value={metrics?.overview?.total_users || 0} 
              change={5.2}
              icon={Users}
            />
            <MetricCard 
              title="Active Users" 
              value={metrics?.overview?.active_users || 0} 
              change={8.1}
              icon={Activity}
            />
            <MetricCard 
              title="Premium Users" 
              value={metrics?.overview?.premium_users || 0} 
              change={12.3}
              icon={CreditCard}
            />
            <MetricCard 
              title="Conversion Rate" 
              value={metrics?.overview?.conversion_rate || 0} 
              change={1.5}
              icon={TrendingUp}
              format="percent"
            />
          </div>
        </div>

        {/* Revenue Metrics */}
        <div>
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Revenue</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard 
              title="Monthly Recurring" 
              value={metrics?.revenue?.mrr || 0} 
              change={23.5}
              icon={CreditCard}
              format="currency"
            />
            <MetricCard 
              title="Annual Recurring" 
              value={metrics?.revenue?.arr || 0} 
              change={23.5}
              icon={BarChart3}
              format="currency"
            />
            <MetricCard 
              title="Growth Rate" 
              value={metrics?.revenue?.growth_rate || 0} 
              icon={TrendingUp}
              format="percent"
            />
            <MetricCard 
              title="Churn Rate" 
              value={metrics?.revenue?.churn_rate || 0} 
              change={-0.3}
              icon={Activity}
              format="percent"
            />
          </div>
        </div>

        {/* Engagement Metrics */}
        <div>
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Engagement</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <MetricCard 
              title="Daily Active" 
              value={metrics?.engagement?.dau || 0} 
              change={15.2}
              icon={Users}
            />
            <MetricCard 
              title="Monthly Active" 
              value={metrics?.engagement?.mau || 0} 
              change={8.1}
              icon={Globe}
            />
            <MetricCard 
              title="Avg Session (min)" 
              value={metrics?.engagement?.avg_session || 0} 
              change={12.0}
              icon={Clock}
            />
            <MetricCard 
              title="7-Day Retention" 
              value={metrics?.engagement?.retention_7d || 0} 
              change={3.2}
              icon={Zap}
              format="percent"
            />
            <MetricCard 
              title="30-Day Retention" 
              value={metrics?.engagement?.retention_30d || 0} 
              change={1.8}
              icon={Zap}
              format="percent"
            />
          </div>
        </div>

        {/* Feature Usage */}
        <div>
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Feature Usage (Last 7 Days)</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <p className="text-3xl font-bold" style={{ color: theme?.colors?.primary }}>
                {(metrics?.features?.allora_chats || 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Allora Conversations</p>
              <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: '85%', backgroundColor: theme?.colors?.primary }} />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <p className="text-3xl font-bold text-blue-600">
                {(metrics?.features?.water_logs || 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Water Logs</p>
              <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: '72%' }} />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <p className="text-3xl font-bold text-green-600">
                {(metrics?.features?.tasks_completed || 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Tasks Completed</p>
              <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full" style={{ width: '58%' }} />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <p className="text-3xl font-bold text-orange-600">
                {(metrics?.features?.health_entries || 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Health Entries</p>
              <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 rounded-full" style={{ width: '45%' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Growth Chart */}
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Growth Trend</h3>
          {metrics?.growth?.length > 0 ? (
            <div className="flex items-end justify-between h-40 gap-2">
              {metrics.growth.map((day, index) => {
                const maxUsers = Math.max(...metrics.growth.map(d => d.users), 1);
                return (
                  <div key={day.date} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full rounded-t transition-all min-h-[4px]"
                      style={{ 
                        height: `${Math.max((day.users / maxUsers) * 100, 3)}%`,
                        backgroundColor: theme?.colors?.primary,
                        opacity: 0.7 + (index * 0.04)
                      }}
                    />
                    <span className="text-xs text-gray-400 mt-2">
                      {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-40 text-gray-400">
              <p>No growth data available yet. Data will appear as users sign up.</p>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="text-center py-4 text-sm text-gray-400">
          Real-time data from database • Last update: {new Date().toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default InvestorDashboard;
